import { useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import {
  downloadLlmTraceCsv,
  downloadLlmTraceDocx,
  fetchLlmTrace,
  type LlmCallTrace,
  type LlmPhaseMetric,
  type LlmTrace,
} from "@/api/tender-api"
import { ChevronDown, ChevronRight, Download, Loader2 } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

import { TenderAiDisclaimer } from "./tender-ai-disclaimer"
import { TENDER_QUERY_KEYS } from "../hooks/use-tender-cases"
import { tenderUiLocaleTag } from "../utils/tender-ui-locale-utils"

interface LlmTraceModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  caseId: string
  runId: string
  caseTitle: string
  step: "extraction" | "bid-review"
  stepLabel: string
}

function formatTokens(n: number, locale: string): string {
  return n.toLocaleString(locale)
}

function formatDuration(
  ms: number,
  t: (key: string, opts?: Record<string, string>) => string
): string {
  if (ms < 1000) return t("tender.llmTrace.durationMs", { ms: String(ms) })
  return t("tender.llmTrace.durationSeconds", { s: (ms / 1000).toFixed(1) })
}

function formatTimestamp(value: string, locale: string): string {
  const timestamp = new Date(value)
  return Number.isNaN(timestamp.getTime()) ? value : timestamp.toLocaleString(locale)
}

function CallCard({ call, index }: { call: LlmCallTrace; index: number }) {
  const { t, i18n } = useTranslation()
  const locale = tenderUiLocaleTag(i18n.language)
  const [systemOpen, setSystemOpen] = useState(false)
  const [userOpen, setUserOpen] = useState(false)
  const [responseOpen, setResponseOpen] = useState(false)

  return (
    <div className="bg-card rounded-lg border p-4">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground text-xs font-semibold">#{index + 1}</span>
          <Badge className="font-mono text-xs" variant="secondary">
            {call.phase}
          </Badge>
          <Badge className="text-xs" variant="outline">
            {call.model}
          </Badge>
        </div>
        <span className="text-muted-foreground shrink-0 text-xs">
          {formatDuration(call.durationMs, t)}
        </span>
      </div>

      <div
        className={`mb-3 grid gap-3 text-center ${call.cachedTokens > 0 ? "grid-cols-4" : "grid-cols-3"}`}
      >
        <div className="bg-muted/50 rounded-md p-2">
          <p className="text-muted-foreground text-[10px] tracking-wide uppercase">
            {t("tender.llmTrace.stats.prompt")}
          </p>
          <p className="text-sm font-semibold tabular-nums">
            {formatTokens(call.promptTokens, locale)}
          </p>
        </div>
        <div className="bg-muted/50 rounded-md p-2">
          <p className="text-muted-foreground text-[10px] tracking-wide uppercase">
            {t("tender.llmTrace.stats.completion")}
          </p>
          <p className="text-sm font-semibold tabular-nums">
            {formatTokens(call.completionTokens, locale)}
          </p>
        </div>
        <div className="bg-primary/5 rounded-md p-2">
          <p className="text-muted-foreground text-[10px] tracking-wide uppercase">
            {t("tender.llmTrace.stats.total")}
          </p>
          <p className="text-sm font-semibold tabular-nums">
            {formatTokens(call.totalTokens, locale)}
          </p>
        </div>
        {call.cachedTokens > 0 && (
          <div className="bg-success/15 rounded-md p-2">
            <p className="text-muted-foreground text-[10px] tracking-wide uppercase">
              {t("tender.llmTrace.stats.cached")}
            </p>
            <p className="text-success text-sm font-semibold tabular-nums">
              {formatTokens(call.cachedTokens, locale)}
            </p>
          </div>
        )}
      </div>

      <div className="text-muted-foreground flex items-center gap-2 text-xs">
        <span>
          {t("tender.llmTrace.tempLabel")} {call.temperature}
        </span>
        <span>·</span>
        <span>{new Date(call.timestamp).toLocaleTimeString(locale)}</span>
      </div>

      <div className="mt-3 space-y-2">
        <PromptSection
          content={call.systemPrompt}
          label={t("tender.llmTrace.promptLabels.system")}
          open={systemOpen}
          onOpenChange={setSystemOpen}
        />
        <PromptSection
          content={call.userMessage}
          open={userOpen}
          label={
            call.userMessageTruncated
              ? t("tender.llmTrace.promptLabels.userTruncated")
              : t("tender.llmTrace.promptLabels.user")
          }
          onOpenChange={setUserOpen}
        />
        <PromptSection
          content={call.responseContent}
          label={t("tender.llmTrace.promptLabels.response")}
          open={responseOpen}
          onOpenChange={setResponseOpen}
        />
      </div>
    </div>
  )
}

function PromptSection({
  label,
  content,
  open,
  onOpenChange,
}: {
  label: string
  content: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <CollapsibleTrigger className="text-muted-foreground hover:bg-muted/50 hover:text-foreground flex w-full items-center gap-1.5 rounded px-2 py-1 text-left text-xs font-medium">
        {open ? (
          <ChevronDown className="h-3 w-3 shrink-0" />
        ) : (
          <ChevronRight className="h-3 w-3 shrink-0" />
        )}
        {label}
        <span className="ml-auto font-normal">
          {t("tender.llmTrace.charCount", { count: content.length })}
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <pre className="bg-muted/60 text-foreground/80 mt-1 max-h-64 overflow-auto rounded p-3 font-mono text-[11px] leading-relaxed break-words whitespace-pre-wrap">
          {content}
        </pre>
      </CollapsibleContent>
    </Collapsible>
  )
}

function SummaryBar({ trace }: { trace: LlmTrace }) {
  const { t, i18n } = useTranslation()
  const locale = tenderUiLocaleTag(i18n.language)
  const callCount = trace.callCount ?? trace.calls.length
  const hasRawCalls = !trace.rawTraceExpiredAt && trace.calls.length > 0
  const totalTokens = trace.calls.reduce((sum, c) => sum + c.totalTokens, 0)
  const totalCachedTokens = trace.calls.reduce((sum, c) => sum + c.cachedTokens, 0)
  const totalDuration = trace.calls.reduce((sum, c) => sum + c.durationMs, 0)
  const models = [...new Set(trace.calls.map((c) => c.model))]

  return (
    <div className="bg-muted/40 flex flex-wrap gap-x-6 gap-y-1 rounded-lg px-4 py-3 text-sm">
      <span>
        <span className="font-semibold">{callCount}</span>{" "}
        <span className="text-muted-foreground">{t("tender.llmTrace.summaryCalls")}</span>
      </span>
      {hasRawCalls && (
        <span>
          <span className="font-semibold">{formatTokens(totalTokens, locale)}</span>{" "}
          <span className="text-muted-foreground">{t("tender.llmTrace.summaryTokensTotal")}</span>
        </span>
      )}
      {hasRawCalls && totalCachedTokens > 0 && (
        <span>
          <span className="text-success font-semibold">
            {formatTokens(totalCachedTokens, locale)}
          </span>{" "}
          <span className="text-muted-foreground">{t("tender.llmTrace.summaryCached")}</span>
        </span>
      )}
      {hasRawCalls && (
        <>
          <span>
            <span className="font-semibold">{formatDuration(totalDuration, t)}</span>{" "}
            <span className="text-muted-foreground">{t("tender.llmTrace.summaryTotalTime")}</span>
          </span>
          <span className="text-muted-foreground">
            {t("tender.llmTrace.summaryModel")}{" "}
            <span className="text-foreground font-medium">{models.join(", ")}</span>
          </span>
        </>
      )}
    </div>
  )
}

function TraceMetadata({ trace }: { trace: LlmTrace }) {
  const { t } = useTranslation()
  const values = [
    [t("tender.llmTrace.metadata.attemptId"), trace.attemptId],
    [t("tender.llmTrace.metadata.attemptKind"), trace.attemptKind],
    [t("tender.llmTrace.metadata.failureReason"), trace.failureReason],
    [t("tender.llmTrace.metadata.pipelineVersion"), trace.pipelineVersion],
  ].filter((entry): entry is [string, string] => Boolean(entry[1]))

  if (values.length === 0) return null

  return (
    <dl className="grid gap-3 rounded-lg border p-4 text-sm sm:grid-cols-2">
      {values.map(([label, value]) => (
        <div key={label} className="min-w-0">
          <dt className="text-muted-foreground text-xs">{label}</dt>
          <dd className="mt-0.5 font-mono text-xs break-all">{value}</dd>
        </div>
      ))}
    </dl>
  )
}

function PhaseMetrics({ metrics }: { metrics: LlmPhaseMetric[] }) {
  const { t, i18n } = useTranslation()
  const locale = tenderUiLocaleTag(i18n.language)

  if (metrics.length === 0) return null

  return (
    <section aria-labelledby="llm-trace-phase-metrics-title" className="space-y-2">
      <h3 className="text-sm font-semibold" id="llm-trace-phase-metrics-title">
        {t("tender.llmTrace.phaseMetrics.title")}
      </h3>
      <div className="space-y-2">
        {metrics.map((metric, index) => (
          <div
            key={`${metric.phase}-${metric.completedAt ?? index}`}
            className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-3 py-2 text-xs"
          >
            <span className="font-mono font-semibold">{metric.phase}</span>
            {metric.outcome && <Badge variant="outline">{metric.outcome}</Badge>}
            {metric.durationMs !== undefined && (
              <span className="text-muted-foreground">{formatDuration(metric.durationMs, t)}</span>
            )}
            {metric.completedAt && (
              <time className="text-muted-foreground" dateTime={metric.completedAt}>
                {formatTimestamp(metric.completedAt, locale)}
              </time>
            )}
            {(metric.failureReason ?? metric.error) && (
              <span className="text-destructive basis-full break-words">
                {metric.failureReason ?? metric.error}
              </span>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}

export function LlmTraceDetails({ trace }: { trace: LlmTrace }) {
  const { t, i18n } = useTranslation()
  const locale = tenderUiLocaleTag(i18n.language)
  const rawTraceExpired = Boolean(trace.rawTraceExpiredAt)

  return (
    <div className="space-y-4 pb-2">
      {trace.rawTraceExpiredAt && (
        <div
          className="border-warning/40 bg-warning/10 text-foreground rounded-lg border p-4 text-sm"
          data-testid="raw-trace-expired"
          role="status"
        >
          <p className="font-semibold">{t("tender.llmTrace.rawTraceExpired.title")}</p>
          <p className="text-muted-foreground mt-1">
            {t("tender.llmTrace.rawTraceExpired.description", {
              expiredAt: formatTimestamp(trace.rawTraceExpiredAt, locale),
            })}
          </p>
        </div>
      )}
      <SummaryBar trace={trace} />
      <TraceMetadata trace={trace} />
      <PhaseMetrics metrics={trace.phaseMetrics} />
      {!rawTraceExpired && trace.calls.length > 0 && (
        <div className="space-y-3">
          {trace.calls.map((call, i) => (
            <CallCard key={`${call.phase}-${i}`} call={call} index={i} />
          ))}
        </div>
      )}
      {!rawTraceExpired && trace.calls.length === 0 && (
        <p className="text-muted-foreground rounded-lg border p-4 text-sm">
          {t("tender.llmTrace.noRawCalls")}
        </p>
      )}
    </div>
  )
}

export function LlmTraceModal({
  open,
  onOpenChange,
  caseId,
  runId,
  caseTitle,
  step,
  stepLabel,
}: LlmTraceModalProps) {
  const { t } = useTranslation()
  const {
    data: trace,
    isLoading,
    isError,
  } = useQuery({
    queryKey: TENDER_QUERY_KEYS.llmTrace(caseId, runId, step),
    queryFn: () => fetchLlmTrace(caseId, runId, step),
    enabled: open,
  })

  const [exporting, setExporting] = useState(false)
  const [exportingCsv, setExportingCsv] = useState(false)

  const handleExportDocx = async () => {
    setExporting(true)
    try {
      await downloadLlmTraceDocx(caseId, runId, { step, caseTitle })
    } finally {
      setExporting(false)
    }
  }

  const handleExportCsv = async () => {
    setExportingCsv(true)
    try {
      await downloadLlmTraceCsv(caseId, runId, { step, caseTitle })
    } finally {
      setExportingCsv(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>{t("tender.llmTrace.modalTitle", { stepLabel })}</DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {isLoading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
            </div>
          )}

          {isError && (
            <div className="bg-destructive/10 text-destructive rounded-lg p-4 text-sm">
              {t("tender.llmTrace.loadError")}
            </div>
          )}

          {trace && <LlmTraceDetails trace={trace} />}
        </div>

        {trace && (
          <DialogFooter className="shrink-0 border-t pt-3">
            <TenderAiDisclaimer className="mr-auto" />
            <Button
              disabled={exportingCsv || Boolean(trace.rawTraceExpiredAt)}
              size="sm"
              variant="outline"
              onClick={handleExportCsv}
            >
              {exportingCsv ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {t("tender.llmTrace.exportCsv")}
            </Button>
            <Button
              disabled={exporting || Boolean(trace.rawTraceExpiredAt)}
              size="sm"
              variant="outline"
              onClick={handleExportDocx}
            >
              {exporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {t("tender.llmTrace.exportDocx")}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
